import { z } from 'zod'
import {
  defaultBackgroundParam,
  validBackgroundParams,
} from '@/components/regionen/pageRegionSlug/hooks/useQueryState/backgroundParam.const'
import {
  parseMapParam,
  serializeMapParam,
  type MapParam,
} from '@/components/regionen/pageRegionSlug/hooks/useQueryState/utils/mapParam'
import { mapParamFallback } from '@/components/regionen/pageRegionSlug/hooks/useQueryState/utils/mapParamFallback.const'
import { zodCalculatorModeParam } from '@/components/regionen/pageRegionSlug/modes/calculator/calculatorModeParam'
import { zodMeasureModeParam } from '@/components/regionen/pageRegionSlug/modes/measure/measureModeParam'
import { zodNotesModeParam } from '@/components/regionen/pageRegionSlug/modes/notes/notesModeParam'
import {
  defaultQaParam,
  zodQaParam,
} from '@/components/regionen/pageRegionSlug/modes/qa/qaConfigStyles'
import { zodReviewListsModeParam } from '@/components/regionen/pageRegionSlug/modes/reviewLists/reviewListsModeParam'
import { zodStreetImageryParam } from '@/components/regionen/pageRegionSlug/streetImagery/streetImageryParam'
import {
  optionalSearchJson,
  optionalSearchBoolean,
  optionalSearchString,
  searchBoolean,
  searchStringArray,
} from '@/lib/searchParamsSchema'
import {
  isPrivateBackgroundParam,
  type PrivateBackgroundParam,
} from '@/server/private-backgrounds/privateBackgroundParam'
import { searchParamsRegistry } from '@/shared/regionen/searchParamsRegistry'

export const defaultMapSearchValue = serializeMapParam(mapParamFallback)

const normalizeMapSearchParam = (raw: unknown) => {
  const wire = optionalSearchString().safeParse(raw).data
  if (!wire) return undefined
  const parsed = parseMapParam(wire)
  return parsed ? serializeMapParam(parsed) : undefined
}

// No default: a defaulted value would make the router redirect to add it before the region loader
// runs, and the loader then redirects again to the region start view (two round trips).
const mapSearchParam = () =>
  z.preprocess(normalizeMapSearchParam, z.string().optional().catch(undefined))

const backgroundSearchParam = () =>
  optionalSearchString()
    .transform((s) => s ?? defaultBackgroundParam)
    .pipe(
      z
        .union([
          z.enum(validBackgroundParams),
          z.custom<PrivateBackgroundParam>(isPrivateBackgroundParam),
        ])
        .catch(defaultBackgroundParam),
    )

export const regionDialogParamSchema = z.enum(['welcome', 'download', 'docs'])

export type RegionDialogParam = z.infer<typeof regionDialogParamSchema>

export const regionSearchSchema = z.object({
  [searchParamsRegistry.v]: optionalSearchString(),
  [searchParamsRegistry.map]: mapSearchParam(),
  [searchParamsRegistry.config]: optionalSearchString(),
  [searchParamsRegistry.data]: searchStringArray().default([]),
  [searchParamsRegistry.f]: optionalSearchString(),
  [searchParamsRegistry.bg]: backgroundSearchParam(),
  [searchParamsRegistry.bg3d]: searchBoolean(false),
  [searchParamsRegistry.debugMap]: optionalSearchBoolean(),
  [searchParamsRegistry.qa]: optionalSearchJson(zodQaParam),
  // Invalid values (e.g. ?dialog=foo) clear rather than throwing the region route into error UI.
  [searchParamsRegistry.dialog]: regionDialogParamSchema.optional().catch(undefined),
  [searchParamsRegistry.welcomeSkipDialog]: z
    .literal(regionDialogParamSchema.enum.welcome)
    .optional()
    .catch(undefined),
  // Mode filters stay on the shared region search so switching modes keeps one URL without
  // per-child validateSearch schemas.
  [searchParamsRegistry.notes]: optionalSearchJson(zodNotesModeParam),
  [searchParamsRegistry.review]: optionalSearchJson(zodReviewListsModeParam),
  [searchParamsRegistry.sum]: optionalSearchJson(zodCalculatorModeParam),
  [searchParamsRegistry.measure]: optionalSearchJson(zodMeasureModeParam),
  [searchParamsRegistry.photos]: optionalSearchJson(zodStreetImageryParam),
})

export type RegionSearch = z.infer<typeof regionSearchSchema>

export const defaultRegionSearch = () => regionSearchSchema.parse({})

export const parseRegionSearch = (search: Record<string, unknown>) =>
  regionSearchSchema.parse(search)

/**
 * The URL viewport, else the start view of the region. A `Link` without `map` has none until the
 * loader redirect is through, and with cached loader data the page already renders then.
 */
export const getMapParamFromSearch = (search: RegionSearch, regionMap: MapParam): MapParam => {
  const wire = search[searchParamsRegistry.map]
  if (!wire || wire === defaultMapSearchValue) return regionMap
  return parseMapParam(wire) ?? regionMap
}

export const getQaParamFromSearch = (search: RegionSearch) => {
  return search[searchParamsRegistry.qa] ?? defaultQaParam
}
